<template>
  <div class="space-y-4">
    <!-- Header com filtros -->
    <div class="flex flex-wrap items-center justify-between gap-3">
      <div class="flex items-center gap-3 flex-wrap">
        <input 
          v-model="termoPesquisa"
          @input="carregarContas"
          type="text"
          :placeholder="t('financeiro.contasPagar.searchPlaceholder')"
          class="filtro-input text-sm"
          style="min-width: 200px"
        />
        <select v-model="filtroStatus" @change="carregarContas" class="filtro-select text-sm">
          <option value="">{{ t('financeiro.contasPagar.filterAll') }}</option>
          <option value="pendente">{{ t('financeiro.contasPagar.filterPending') }}</option>
          <option value="pago">{{ t('financeiro.contasPagar.filterPaid') }}</option>
        </select>
        <select v-model="filtroMensal" @change="carregarContas" class="filtro-select text-sm">
          <option value="">{{ t('financeiro.contasPagar.filterAll') }}</option>
          <option value="true">{{ t('financeiro.contasPagar.filterMonthly') }}</option>
          <option value="false">{{ t('financeiro.contasPagar.filterOneOff') }}</option>
        </select>
        <span class="text-xs font-medium" style="color: var(--text-secondary)">{{ t('financeiro.contasPagar.from') }}</span>
        <input 
          v-model="dataInicio"
          @change="carregarContas"
          type="date"
          class="filtro-input text-sm"
          style="width: 145px"
        />
        <span class="text-xs font-medium" style="color: var(--text-secondary)">{{ t('financeiro.contasPagar.to') }}</span>
        <input 
          v-model="dataFim"
          @change="carregarContas"
          type="date"
          class="filtro-input text-sm"
          style="width: 145px"
        />
      </div>
      <button @click="abrirModalNovo" class="btn-success text-sm">
        ➕ {{ t('financeiro.contasPagar.newAccount') }}
      </button>
    </div>

    <!-- Resumo -->
    <div class="grid grid-cols-1 md:grid-cols-4 gap-4">
      <div class="card-resumo">
        <p class="card-resumo-label">{{ t('financeiro.contasPagar.totalAccounts') }}</p>
        <p class="card-resumo-valor" style="color: var(--text-primary)">{{ contas.length }}</p>
      </div>
      <div class="card-resumo">
        <p class="card-resumo-label">{{ t('financeiro.contasPagar.totalValue') }}</p>
        <MoneyAmount :value="totalGeral" tag="p" class="card-resumo-valor text-blue-600" />
      </div>
      <div class="card-resumo">
        <p class="card-resumo-label">{{ t('financeiro.contasPagar.totalPaid') }}</p>
        <MoneyAmount :value="totalPago" tag="p" class="card-resumo-valor text-green-600" />
      </div>
      <div class="card-resumo">
        <p class="card-resumo-label">{{ t('financeiro.contasPagar.totalPending') }}</p>
        <MoneyAmount :value="totalPendente" tag="p" class="card-resumo-valor text-orange-600" />
      </div>
    </div>

    <!-- Lista de contas -->
    <div class="space-y-3">
      <div v-if="contas.length === 0" class="text-center py-12" style="color: var(--text-secondary)">
        <p class="text-4xl mb-3">💰</p>
        <p class="text-lg font-medium">{{ t('financeiro.contasPagar.emptyTitle') }}</p>
        <p class="text-sm mt-1">{{ t('financeiro.contasPagar.emptyHint') }}</p>
      </div>

      <div v-for="conta in contas" :key="conta.id" class="conta-card" :class="{ 'conta-paga': conta.isPago }">
        <div class="conta-header">
          <div class="conta-info">
            <div class="flex items-center gap-2 flex-wrap">
              <h3 class="text-base font-semibold" style="color: var(--text-primary)">
                {{ conta.isPago ? '✅' : '⏳' }} {{ conta.nome }}
              </h3>
              <span v-if="conta.isMensal" class="tag-mensal">🔄 {{ t('financeiro.contasPagar.monthly') }}</span>
              <span :class="conta.isPago ? 'tag-pago' : 'tag-pendente'">
                {{ conta.isPago ? t('financeiro.contasPagar.paid') : t('financeiro.contasPagar.pending') }}
              </span>
            </div>
            <p v-if="conta.descricao" class="text-sm mt-1" style="color: var(--text-secondary)">{{ conta.descricao }}</p>
            <div class="flex items-center gap-3 mt-2 flex-wrap">
              <span class="conta-chip">📅 {{ formatDate(conta.dataVencimento) }}</span>
              <span v-if="conta.dataPagamento" class="conta-chip">✅ {{ t('financeiro.contasPagar.paidOn') }} {{ formatDate(conta.dataPagamento) }}</span>
              <span v-if="conta.mesReferencia && conta.anoReferencia" class="conta-chip">
                📆 {{ t('financeiro.contasPagar.ref') }}: {{ String(conta.mesReferencia).padStart(2, '0') }}/{{ conta.anoReferencia }}
              </span>
            </div>
          </div>
          <div class="flex items-center gap-2">
            <MoneyAmount
              :value="conta.valor"
              class="conta-valor"
              :class="conta.isPago ? 'text-green-600' : 'text-orange-600'"
            />
          </div>
        </div>

        <div class="conta-actions">
          <button v-if="!conta.isPago" @click="marcarPago(conta)" class="conta-btn conta-btn-success" :title="t('financeiro.contasPagar.markPaidTitle')">
            ✅ {{ t('financeiro.contasPagar.markPaid') }}
          </button>
          <button v-else @click="desmarcarPago(conta)" class="conta-btn conta-btn-secondary" :title="t('financeiro.contasPagar.undoTitle')">
            ↩️ {{ t('financeiro.contasPagar.undo') }}
          </button>
          <a v-if="conta.urlComprovante" :href="conta.urlComprovante" target="_blank" class="conta-btn conta-btn-info" :title="t('financeiro.contasPagar.receiptTitle')">
            📎 {{ t('financeiro.contasPagar.receipt') }}
          </a>
          <button @click="abrirModalEditar(conta)" class="conta-btn conta-btn-secondary" title="Editar">
            ✏️
          </button>
          <button v-if="!conta.isPago" @click="excluirConta(conta)" class="conta-btn conta-btn-danger" title="Excluir">
            🗑️
          </button>
        </div>
      </div>
    </div>

    <!-- Modal Criar/Editar -->
    <div v-if="modalAberto" class="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50" @click.self="fecharModal">
      <div class="modal-panel bg-white dark:bg-gray-800 rounded-xl shadow-2xl w-[500px] max-h-[90vh] overflow-y-auto p-6">
        <div class="flex items-center justify-between mb-4">
          <h2 class="text-xl font-bold" style="color: var(--text-primary)">
            {{ contaEditando ? `✏️ ${t('financeiro.contasPagar.editAccount')}` : `➕ ${t('financeiro.contasPagar.newAccount')}` }}
          </h2>
          <button @click="fecharModal" class="p-1 hover:bg-gray-200 dark:hover:bg-gray-700 rounded-lg">
            <svg class="w-5 h-5" style="color: var(--text-secondary)" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
            </svg>
          </button>
        </div>

        <div class="space-y-4">
          <!-- Nome -->
          <div>
            <label class="block text-sm font-medium mb-1" style="color: var(--text-primary)">{{ t('common.name') }} *</label>
            <input v-model="formData.nome" type="text" :placeholder="t('financeiro.contasPagar.namePlaceholder')"
              class="w-full px-3 py-2 text-sm rounded-lg border transition-all duration-200"
              style="background: var(--bg-secondary); color: var(--text-primary); border-color: var(--border)"
            />
          </div>

          <!-- {{ t('common.description') }} -->
          <div>
            <label class="block text-sm font-medium mb-1" style="color: var(--text-primary)">{{ t('common.description') }}</label>
            <textarea v-model="formData.descricao" rows="2" placeholder="..."
              class="w-full px-3 py-2 text-sm rounded-lg border resize-none transition-all duration-200"
              style="background: var(--bg-secondary); color: var(--text-primary); border-color: var(--border)"
            ></textarea>
          </div>

          <!-- Valor -->
          <div>
            <label class="block text-sm font-medium mb-1" style="color: var(--text-primary)">{{ t('financeiro.contasPagar.valueBrl') }} *</label>
            <div class="money-input-with-prefix">
              <MoneyInputPrefix />
              <input v-model.number="formData.valor" type="number" step="0.01" min="0" :placeholder="t('common.moneyZero')"
                class="w-full pl-10 pr-3 py-2 text-sm rounded-lg border transition-all duration-200"
                style="background: var(--bg-secondary); color: var(--text-primary); border-color: var(--border)"
              />
            </div>
          </div>

          <!-- Data de Vencimento -->
          <div>
            <label class="block text-sm font-medium mb-1" style="color: var(--text-primary)">{{ t('financeiro.contasPagar.dueDate') }} *</label>
            <input v-model="formData.dataVencimento" type="date"
              class="w-full px-3 py-2 text-sm rounded-lg border transition-all duration-200"
              style="background: var(--bg-secondary); color: var(--text-primary); border-color: var(--border)"
            />
          </div>

          <!-- Is Mensal -->
          <div class="flex items-center gap-3">
            <label class="relative inline-flex items-center cursor-pointer">
              <input type="checkbox" v-model="formData.isMensal" class="sr-only peer" />
              <div class="w-11 h-6 bg-gray-300 dark:bg-gray-600 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-emerald-500"></div>
            </label>
            <span class="text-sm font-medium" style="color: var(--text-primary)">🔄 {{ t('financeiro.contasPagar.monthlyAccount') }}</span>
            <span v-if="formData.isMensal" class="text-xs px-2 py-1 rounded-full bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400">
              {{ t('financeiro.contasPagar.monthlyRepeat') }}
            </span>
          </div>

          <!-- Is Pago -->
          <div class="flex items-center gap-3">
            <label class="relative inline-flex items-center cursor-pointer">
              <input type="checkbox" v-model="formData.isPago" class="sr-only peer" />
              <div class="w-11 h-6 bg-gray-300 dark:bg-gray-600 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-500"></div>
            </label>
            <span class="text-sm font-medium" style="color: var(--text-primary)">✅ {{ t('financeiro.contasPagar.alreadyPaid') }}</span>
          </div>

          <!-- URL Comprovante -->
          <div>
            <label class="block text-sm font-medium mb-1" style="color: var(--text-primary)">{{ t('financeiro.contasPagar.receiptUrl') }}</label>
            <input v-model="formData.urlComprovante" type="url" placeholder="https://..."
              class="w-full px-3 py-2 text-sm rounded-lg border transition-all duration-200"
              style="background: var(--bg-secondary); color: var(--text-primary); border-color: var(--border)"
            />
          </div>
        </div>

        <!-- Botões -->
        <div class="flex justify-end gap-3 mt-6 pt-4 border-t" style="border-color: var(--border)">
          <button @click="fecharModal" class="px-4 py-2 text-sm font-medium rounded-lg transition-all"
            style="background: var(--bg-secondary); color: var(--text-primary); border: 1px solid var(--border)">
            {{ t('common.cancel') }}
          </button>
          <button @click="salvarConta" class="px-4 py-2 text-sm font-medium rounded-lg bg-emerald-600 text-white hover:bg-emerald-700 transition-all">
            {{ contaEditando ? t('financeiro.contasPagar.saveChanges') : t('financeiro.contasPagar.createAccount') }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import { useI18n } from 'vue-i18n'
import { intlLocaleFor } from '../../utils/format'
import MoneyAmount from '../MoneyAmount.vue'
import MoneyInputPrefix from '../MoneyInputPrefix.vue'
import api from '../../services/api'
import Swal from 'sweetalert2'

const { t, locale } = useI18n()

const emit = defineEmits(['atualizar'])

const contas = ref([])
const termoPesquisa = ref('')
const filtroStatus = ref('')
const filtroMensal = ref('')
const modalAberto = ref(false)
const contaEditando = ref(null)

// Datas padrão: mês atual
const now = new Date()
const anoAtual = now.getFullYear()
const mesAtual = String(now.getMonth() + 1).padStart(2, '0')
const dataInicio = ref(`${anoAtual}-${mesAtual}-01`)
const ultimoDia = new Date(anoAtual, now.getMonth() + 1, 0).getDate()
const dataFim = ref(`${anoAtual}-${mesAtual}-${String(ultimoDia).padStart(2, '0')}`)

const formData = ref({
  nome: '',
  descricao: '',
  valor: null,
  dataVencimento: '',
  isMensal: false,
  isPago: false,
  urlComprovante: ''
})

// Computed
const totalGeral = computed(() => contas.value.reduce((sum, c) => sum + (parseFloat(c.valor) || 0), 0))
const totalPago = computed(() => contas.value.filter(c => c.isPago).reduce((sum, c) => sum + (parseFloat(c.valor) || 0), 0))
const totalPendente = computed(() => contas.value.filter(c => !c.isPago).reduce((sum, c) => sum + (parseFloat(c.valor) || 0), 0))

function formatDate(date) {
  if (!date) return ''
  if (Array.isArray(date)) {
    const [y, m, d] = date
    return `${String(d).padStart(2, '0')}/${String(m).padStart(2, '0')}/${y}`
  }
  const d = new Date(date + 'T00:00:00')
  return d.toLocaleDateString(intlLocaleFor(locale.value))
}

// CRUD
async function carregarContas() {
  try {
    const params = {}
    if (termoPesquisa.value && termoPesquisa.value.trim()) {
      params.searchTerm = termoPesquisa.value.trim()
    }
    if (filtroStatus.value === 'pago') params.isPago = true
    if (filtroStatus.value === 'pendente') params.isPago = false
    if (filtroMensal.value === 'true') params.isMensal = true
    if (filtroMensal.value === 'false') params.isMensal = false
    if (dataInicio.value) params.dataInicio = dataInicio.value
    if (dataFim.value) params.dataFim = dataFim.value

    const response = await api.get('/financeiro/contas-pagar', { params })
    contas.value = response.data
  } catch (error) {
    console.error('Erro ao carregar contas:', error)
    Swal.fire('Erro', t('financeiro.contasPagar.loadError'), 'error')
  }
}

function abrirModalNovo() {
  contaEditando.value = null
  formData.value = {
    nome: '', descricao: '', valor: null, dataVencimento: '',
    isMensal: false, isPago: false, urlComprovante: ''
  }
  modalAberto.value = true
}

function abrirModalEditar(conta) {
  contaEditando.value = conta
  formData.value = {
    nome: conta.nome,
    descricao: conta.descricao || '',
    valor: conta.valor,
    dataVencimento: Array.isArray(conta.dataVencimento) 
      ? `${conta.dataVencimento[0]}-${String(conta.dataVencimento[1]).padStart(2,'0')}-${String(conta.dataVencimento[2]).padStart(2,'0')}`
      : conta.dataVencimento,
    isMensal: conta.isMensal || false,
    isPago: conta.isPago || false,
    urlComprovante: conta.urlComprovante || ''
  }
  modalAberto.value = true
}

function fecharModal() {
  modalAberto.value = false
  contaEditando.value = null
}

async function salvarConta() {
  if (!formData.value.nome || !formData.value.valor || !formData.value.dataVencimento) {
    Swal.fire('Atenção', t('financeiro.contasPagar.requiredFields'), 'warning')
    return
  }

  try {
    if (contaEditando.value) {
      await api.put(`/financeiro/contas-pagar/${contaEditando.value.id}`, formData.value)
      Swal.fire({ icon: 'success', title: t('financeiro.contasPagar.accountUpdated'), toast: true, position: 'top-end', timer: 2000, showConfirmButton: false })
    } else {
      await api.post('/financeiro/contas-pagar', formData.value)
      Swal.fire({ icon: 'success', title: t('financeiro.contasPagar.accountCreated'), toast: true, position: 'top-end', timer: 2000, showConfirmButton: false })
    }
    fecharModal()
    carregarContas()
    emit('atualizar')
  } catch (error) {
    console.error('Erro ao salvar conta:', error)
    Swal.fire('Erro', t('financeiro.contasPagar.saveError'), 'error')
  }
}

async function marcarPago(conta) {
  try {
    await api.put(`/financeiro/contas-pagar/${conta.id}/marcar-pago`, {})
    Swal.fire({ icon: 'success', title: t('financeiro.contasPagar.markedPaid'), toast: true, position: 'top-end', timer: 2000, showConfirmButton: false })
    carregarContas()
    emit('atualizar')
  } catch (error) {
    console.error('Erro ao marcar como pago:', error)
    Swal.fire('Erro', t('financeiro.contasPagar.markPaidError'), 'error')
  }
}

async function desmarcarPago(conta) {
  try {
    await api.put(`/financeiro/contas-pagar/${conta.id}/desmarcar-pago`, {})
    Swal.fire({ icon: 'info', title: t('financeiro.contasPagar.paymentUndone'), toast: true, position: 'top-end', timer: 2000, showConfirmButton: false })
    carregarContas()
    emit('atualizar')
  } catch (error) {
    console.error('Erro ao desmarcar pago:', error)
  }
}

async function excluirConta(conta) {
  const isMensalPai = conta.isMensal && !conta.contaPaiId
  const result = await Swal.fire({
    title: t('financeiro.contasPagar.deleteTitle'),
    html: isMensalPai 
      ? `<b>${conta.nome}</b><br><br>⚠️ ${t('financeiro.contasPagar.deleteMonthlyWarning')}`
      : `Deseja excluir <b>${conta.nome}</b>?`,
    icon: 'warning',
    showCancelButton: true,
    confirmButtonColor: '#ef4444',
    cancelButtonColor: '#6b7280',
    confirmButtonText: t('financeiro.contasPagar.deleteYes'),
    cancelButtonText: t('common.cancel')
  })

  if (result.isConfirmed) {
    try {
      await api.delete(`/financeiro/contas-pagar/${conta.id}`)
      Swal.fire({ icon: 'success', title: t('financeiro.contasPagar.accountDeleted'), toast: true, position: 'top-end', timer: 2000, showConfirmButton: false })
      carregarContas()
      emit('atualizar')
    } catch (error) {
      console.error('Erro ao excluir conta:', error)
      Swal.fire('Erro', t('financeiro.contasPagar.deleteError'), 'error')
    }
  }
}

onMounted(() => {
  carregarContas()
})
</script>

<style scoped>
.btn-success {
  @apply px-4 py-2 rounded-lg font-medium transition-all bg-emerald-600 text-white hover:bg-emerald-700;
}

.filtro-select,
.filtro-input {
  background-color: var(--bg-secondary);
  color: var(--text-primary);
  border: 1px solid var(--border);
  border-radius: 10px;
  padding: 0.6rem 0.9rem;
  transition: box-shadow 0.2s ease, border-color 0.2s ease;
}

.filtro-select:focus,
.filtro-input:focus {
  outline: none;
  border-color: #10b981;
  box-shadow: 0 0 0 3px rgba(16, 185, 129, 0.2);
}

.card-resumo {
  background-color: var(--bg-secondary);
  border: 1px solid var(--border);
  border-radius: 12px;
  padding: 1rem 1.25rem;
}

.card-resumo-label {
  color: var(--text-secondary);
  font-size: 0.75rem;
  margin-bottom: 0.25rem;
}

.card-resumo-valor {
  font-weight: 700;
  font-size: 1.25rem;
}

.conta-card {
  background-color: var(--bg-secondary);
  border: 1px solid var(--border);
  border-radius: 14px;
  padding: 1rem 1.25rem;
  transition: box-shadow 0.2s ease, transform 0.2s ease;
}

.conta-card:hover {
  box-shadow: 0 8px 20px rgba(0, 0, 0, 0.1);
  transform: translateY(-1px);
}

.conta-paga {
  opacity: 0.75;
  border-left: 4px solid #10b981;
}

.conta-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 1rem;
}

.conta-info {
  flex: 1;
  min-width: 0;
}

.conta-valor {
  font-weight: 700;
  font-size: 1.1rem;
  white-space: nowrap;
}

.conta-chip {
  font-size: 0.75rem;
  padding: 0.2rem 0.5rem;
  border-radius: 999px;
  background-color: var(--bg-primary);
  color: var(--text-secondary);
  border: 1px solid var(--border);
}

.tag-mensal {
  font-size: 0.7rem;
  padding: 0.15rem 0.5rem;
  border-radius: 999px;
  background-color: rgba(16, 185, 129, 0.15);
  color: #059669;
  font-weight: 600;
}

.tag-pago {
  font-size: 0.7rem;
  padding: 0.15rem 0.5rem;
  border-radius: 999px;
  background-color: rgba(16, 185, 129, 0.15);
  color: #059669;
  font-weight: 600;
}

.tag-pendente {
  font-size: 0.7rem;
  padding: 0.15rem 0.5rem;
  border-radius: 999px;
  background-color: rgba(245, 158, 11, 0.15);
  color: #d97706;
  font-weight: 600;
}

.conta-actions {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-top: 0.75rem;
  flex-wrap: wrap;
}

.conta-btn {
  padding: 0.3rem 0.7rem;
  border-radius: 8px;
  font-size: 0.75rem;
  font-weight: 600;
  transition: all 0.2s ease;
  cursor: pointer;
}

.conta-btn-success {
  background-color: #16a34a;
  color: #fff;
}
.conta-btn-success:hover {
  background-color: #15803d;
}

.conta-btn-info {
  background-color: #2563eb;
  color: #fff;
  text-decoration: none;
}
.conta-btn-info:hover {
  background-color: #1d4ed8;
}

.conta-btn-secondary {
  background-color: var(--bg-primary);
  color: var(--text-primary);
  border: 1px solid var(--border);
}
.conta-btn-secondary:hover {
  background-color: var(--bg-secondary);
}

.conta-btn-danger {
  background-color: transparent;
  color: #ef4444;
  border: 1px solid rgba(239, 68, 68, 0.3);
}
.conta-btn-danger:hover {
  background-color: rgba(239, 68, 68, 0.1);
}
</style>
